<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row justify="start" align="center">
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.exam_year_batch"
          :items="examBatches"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Exam Batch"
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.year_batch"
          :items="batches"
          outlined
          dense
          hide-details="auto"
          label="Batch"
          clearable
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.program"
          label="Program"
          :items="programs"
          :item-text="
            (item) =>
              `${item.degree.short_name}${
                item.specialization ? ` - ${item.specialization}` : ''
              }`
          "
          return-object
          outlined
          dense
          clearable
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model.trim="form.usn_id"
          label="USN"
          outlined
          dense
          clearable
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model="form.course_code"
          label="Course Code"
          outlined
          dense
          hide-details="auto"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model="form.term_no"
          label="Subject Semester"
          type="number"
          outlined
          dense
          clearable
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12" sm="3" class="d-flex justify-start">
        <v-btn
          class="accent white--text mr-2"
          :loading="loading"
          @click="search(true)"
        >
          <v-icon left small>mdi-magnify</v-icon>
          Search
        </v-btn>
        <v-btn
          class="accent white--text mr-2"
          :loading="downloadingTemplate"
          @click="downloadTemplate"
        >
          <v-icon left small>mdi-download</v-icon>
          Download
        </v-btn>
        <v-btn class="accent white--text mr-2" @click="openUploadDialog">
          <v-icon left small>mdi-upload</v-icon>
          Upload
        </v-btn>
      </v-col>
    </v-row>

    <!-- ── Table ────────────────────────────────────────────────────────── -->
    <v-row class="mt-3">
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :items="externalMarks"
            :server-items-length="totalRecords"
            :options.sync="tableOptions"
            :footer-props="{ 'items-per-page-options': [10, 50, 100] }"
            :loading="loading"
            fixed-header
            class="secondary row-height-50"
            @update:options="onTableOptionsChange"
          >
            <template #[`item.mark`]="{ item }">
              {{ item.mark ?? "-" }}
            </template>

            <template #[`item.internal_mark`]="{ item }">
              {{ item.internal_mark ?? "-" }}
            </template>

            <template #[`item.result`]="{ item }">
              <v-chip
                v-if="item.result"
                x-small
                :color="resultColor(item.result)"
                dark
              >
                {{ item.result }}
              </v-chip>
              <span v-else>-</span>
            </template>

            <template #[`item.action`]="{ item }">
              <v-icon small color="accent" @click="editMark(item)">
                mdi-pencil
              </v-icon>
            </template>

            <template #no-data>
              <div class="text-center grey--text py-4">
                No external marks found. Apply filters and click Search.
              </div>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <!-- ══════════════════════════════════════════════════════════════════ -->
    <!-- Upload Dialog                                                      -->
    <!-- ══════════════════════════════════════════════════════════════════ -->
    <v-dialog v-model="uploadDialog" max-width="700" persistent scrollable>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          Upload External Marks
          <v-spacer></v-spacer>
          <v-btn small icon @click="closeUploadDialog">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-4">
          <!-- Required fields -->
          <v-autocomplete
            v-model="form.exam_year_batch"
            :items="examBatches"
            label="Exam Batch *"
            outlined
            dense
            hide-details="auto"
            class="mb-3"
          />

          <v-divider class="my-4" />

          <v-file-input
            v-model="uploadForm.file"
            label="Select filled CSV"
            outlined
            dense
            accept=".csv"
            hide-details="auto"
            prepend-inner-icon="mdi-file-upload"
            prepend-icon=""
          >
            <template v-slot:selection="{ text }">
              <span class="text-body-2 text-truncate" style="max-width: 100%">
                {{ uploadForm.file ? uploadForm.file.name : text }}
              </span>
            </template>
          </v-file-input>

          <!-- Grace Mark — shown after "Check Results". Enter a common value
               (e.g. 4 or 5) and click "Recalculate" below to preview its
               effect on pass/fail before saving. A student's own "Grace Mark"
               column in the sheet always overrides this common value. For an
               arrear paper, the sheet also has a "Use Scaled External"
               column (Y/N) — an alternative to grace mark that scales the
               external paper alone to 100 instead. Both scenarios' totals
               and % are shown in the downloaded sheet/result so the reviewer
               can compare before choosing either one (not both). -->
          <v-text-field
            v-if="summaryGenerated"
            v-model.number="uploadForm.grace_mark"
            label="Grace Mark (common)"
            type="number"
            min="0"
            outlined
            dense
            clearable
            hide-details="auto"
            class="mt-4"
            hint="e.g. 4 or 5 — applied only to students without a Grace Mark of their own in the sheet, then click Recalculate. For arrears, 'Use Scaled External' in the sheet is an alternative — see Download Result for both scenarios."
            persistent-hint
          />

          <!-- Summary chips — shown after "Check Results" -->
          <div v-if="summaryGenerated" class="mt-4">
            <v-chip color="primary" outlined class="ma-1">
              <v-icon left small>mdi-account-group</v-icon>
              Total: {{ summary.total_students }}
            </v-chip>
            <v-chip color="success" outlined class="ma-1">
              <v-icon left small>mdi-check-circle</v-icon>
              Pass: {{ summary.pass }}
            </v-chip>
            <v-chip color="error" outlined class="ma-1">
              <v-icon left small>mdi-close-circle</v-icon>
              Fail: {{ summary.fail }}
            </v-chip>
            <v-chip color="info" outlined class="ma-1">
              <v-icon left small>mdi-chart-pie</v-icon>
              Pass %: {{ summary.pass_percentage }}%
            </v-chip>
            <v-chip
              v-if="summary.arrear_students"
              color="warning"
              outlined
              class="ma-1"
            >
              <v-icon left small>mdi-history</v-icon>
              Arrear: {{ summary.arrear_students }}
            </v-chip>
            <v-chip
              v-if="summary.passed_by_grace_mark"
              outlined
              class="ma-1"
            >
              Passed by Grace Mark: {{ summary.passed_by_grace_mark }}
            </v-chip>
            <v-chip
              v-if="summary.passed_by_scaled_external"
              outlined
              class="ma-1"
            >
              Passed by Scaled External: {{ summary.passed_by_scaled_external }}
            </v-chip>
          </div>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />

          <!-- Check Results (dry-run) -->
          <v-btn
            v-if="!summaryGenerated"
            color="primary"
            :disabled="!canCheckResults"
            :loading="checkingResults"
            @click="checkResults"
          >
            <v-icon left small>mdi-calculator</v-icon>
            Check Results
          </v-btn>

          <!-- Recalculate -->
          <v-btn
            v-if="summaryGenerated"
            color="primary"
            outlined
            :disabled="!canCheckResults"
            :loading="checkingResults"
            @click="checkResults"
          >
            Recalculate
          </v-btn>
          <!-- Download Result — enabled once summary/csv is ready -->
          <v-btn
            v-if="summaryGenerated"
            color="info"
            outlined
            class="ml-2"
            :loading="downloadingResult"
            :disabled="!resultCsv"
            @click="downloadResult"
          >
            <v-icon left small>mdi-download</v-icon>
            Download Result
          </v-btn>
          <!-- Save — only shown after summary is generated -->
          <v-btn
            v-if="summaryGenerated"
            color="success"
            class="ml-2"
            :loading="saving"
            @click="submitUpload"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Save External Marks
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══════════════════════════════════════════════════════════════════ -->
    <!-- Edit Mark Dialog                                                   -->
    <!-- ══════════════════════════════════════════════════════════════════ -->
    <v-dialog v-model="editDialog" max-width="400" persistent>
      <v-card v-if="editDialog">
        <v-form ref="form">
          <v-card-title class="font-weight-medium secondary--text primary">
            Edit External Mark
            <v-spacer></v-spacer>
            <v-btn small icon @click="editDialog = false">
              <v-icon small color="secondary">mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editingItem.usn_id"
                label="USN"
                outlined
                dense
                hide-details="auto"
                readonly
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editingItem.course_code"
                label="Course Code"
                outlined
                dense
                hide-details="auto"
                readonly
              />
            </v-col>
            <v-col cols="12" sm="12">
              <v-textarea
                v-model="editingItem.course_desc"
                label="Course Name"
                outlined
                dense
                hide-details="auto"
                rows="2"
                readonly
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="editingItem.mark"
                label="Mark*"
                type="number"
                outlined
                dense
                hide-details="auto"
                :rules="[rules.acceptZero]"
              />
            </v-col>
          </v-card-text>
          <v-card-actions class="pa-3">
            <v-spacer />
            <v-btn color="primary" :loading="savingEdit" @click="saveEdit">
              Save
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { studentExternalMarkHeaders } from "@/constants/headers";
import { rules } from "@/helpers/rules";

export default {
  name: "ExternalMarks",

  data() {
    return {
      // ── Filter form ──────────────────────────────────────────────────
      form: {
        year_batch: null,
        exam_year_batch: null,
        program: null,
        usn_id: "",
        course_code: "",
        term_no: null,
      },

      // ── Dropdown data ────────────────────────────────────────────────
      examBatches: [],
      programs: [],

      // ── Table ────────────────────────────────────────────────────────
      externalMarks: [],
      loading: false,
      tableSearch: "",
      headers: studentExternalMarkHeaders,
      totalRecords: 0,
      tableOptions: { page: 1, itemsPerPage: 10 },
      isResetting: false,

      // ── Upload dialog ────────────────────────────────────────────────
      uploadDialog: false,
      uploadForm: {
        file: null,
        grace_mark: null,
      },
      downloadingTemplate: false,
      checkingResults: false,
      saving: false,
      summaryGenerated: false,
      summary: {},

      // ── Edit dialog ──────────────────────────────────────────────────
      editDialog: false,
      editingItem: {},
      savingEdit: false,
      rules,
      resultCsv: null,
      downloadingResult: false,
      batches: [],
    };
  },

  computed: {
    canCheckResults() {
      return !!this.form.exam_year_batch && !!this.uploadForm.file;
    },

    uploadYear() {
      return this.form?.exam_year_batch
        ? this.form.exam_year_batch.split("-")[0]
        : null;
    },

    uploadMonth() {
      return this.form?.exam_year_batch
        ? this.form.exam_year_batch.split("-")[1]
        : null;
    },

    searchParams() {
      const { program, ...params } = this.form;
      if (program) {
        params.program_id = program.id;
      }
      return params;
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "External" }]);
    this.init();
  },

  methods: {
    resultColor(result) {
      return (
        {
          Pass: "success",
          Fail: "error",
          Absent: "warning",
          Detained: "grey darken-1",
        }[result] ?? "grey"
      );
    },

    // ── Init ─────────────────────────────────────────────────────────────
    async init() {
      await Promise.all([
        this.getExamBatches(),
        this.getPrograms(),
        this.generateBatches(),
      ]);
      this.search();
    },
    async generateBatches() {
      try {
        const response = await this.$axios.get("/get_year_batch");
        this.batches = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getExamBatches() {
      await this.$axios
        .get("/exam_external_batches")
        .then((res) => {
          this.examBatches = res.data.data;
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message:
              err.response?.data?.message || "Failed to load exam batches",
            color: "error",
          });
        });
    },

    async getPrograms() {
      await this.$axios
        .get("/programs")
        .then((res) => {
          this.programs = res.data.data;
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message: err.response?.data?.message || "Failed to load programs",
            color: "error",
          });
        });
    },

    // ── Pagination ───────────────────────────────────────────────────────
    onTableOptionsChange(options) {
      if (this.isResetting) return;
      this.tableOptions = options;
      this.search();
    },

    // ── Search ───────────────────────────────────────────────────────────
    async search(resetPage = false) {
      if (resetPage) {
        this.isResetting = true;
        this.tableOptions = { ...this.tableOptions, page: 1 };
        this.$nextTick(() => {
          this.isResetting = false;
        });
      }

      this.loading = true;
      this.externalMarks = [];
      const queryString = this.objectToQueryParams({
        ...this.searchParams,
        page: this.tableOptions.page,
        per_page: this.tableOptions.itemsPerPage,
      });
      await this.$axios
        .get(`/external_marks?${queryString}`)
        .then((res) => {
          this.externalMarks = res.data.data.records;
          this.totalRecords = res.data.data.total;
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message:
              err.response?.data?.message || "Failed to load external marks",
            color: "error",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // ── Upload dialog ────────────────────────────────────────────────────
    openUploadDialog() {
      this.uploadForm.file = null;
      this.uploadForm.grace_mark = null;
      this.summaryGenerated = false;
      this.summary = {};
      this.resultCsv = null;
      this.uploadDialog = true;
    },

    closeUploadDialog() {
      this.uploadDialog = false;
      this.uploadForm = { file: null, grace_mark: null };
      this.summaryGenerated = false;
      this.summary = {};
      this.resultCsv = null;
    },
    objectToQueryParams(obj) {
      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = year;
        obj.batch = batch;
        delete obj.year_batch;
      }
      if (obj.exam_year_batch) {
        const [year, month] = obj.exam_year_batch.split("-");
        obj.exam_year = year;
        obj.exam_month = month;
        delete obj.exam_year_batch;
      }
      return Object.keys(obj)
        .filter(
          (key) =>
            obj[key] !== undefined && obj[key] !== null && obj[key] !== "",
        )
        .map(
          (key) => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]),
        )
        .join("&");
    },

    async downloadTemplate() {
      this.downloadingTemplate = true;

      const label = `External Mark`;
      const queryString = this.objectToQueryParams({
        ...this.searchParams,
        type: "external",
      });

      await this.$axios
        .get(`/download_marks?${queryString}`, { responseType: "blob" })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${label}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message: err.response?.data?.message || "Download failed",
            color: "error",
          });
        })
        .finally(() => {
          this.downloadingTemplate = false;
        });
    },

    // Dry-run: returns pass/fail summary without saving
    async checkResults() {
      if (!this.uploadForm.file) return;
      this.checkingResults = true;
      this.summaryGenerated = false;

      const formData = this.buildFormData("summary");

      await this.$axios
        .post("/upload_marks", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
          validateStatus: (s) => s < 500,
        })
        .then((res) => {
          if (res.status >= 400) {
            this.showErrorResponse(res.data, "Check failed");
            return;
          }
          if (res.status === 201) {
            this.downloadErrorCsv(res.data);
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            const data = JSON.parse(reader.result);
            this.summary = data.data.summary;
            this.resultCsv = data.data.csv;
            this.summaryGenerated = true;
          };
          reader.readAsText(res.data);
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message: err.response?.data?.message || "Check failed",
            color: "error",
          });
        })
        .finally(() => {
          this.checkingResults = false;
        });
    },

    // Actual save
    async submitUpload() {
      if (!this.uploadForm.file) return;
      

      this.saving = true;
      const formData = this.buildFormData("submit");

      await this.$axios
        .post("/upload_marks", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
          validateStatus: (s) => s < 500,
        })
        .then((res) => {
          if (res.status >= 400) {
            this.showErrorResponse(res.data, "Upload failed");
            return;
          }
          if (res.status === 201) {
            this.downloadErrorCsv(res.data);
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            let paidLater = [];
            try {
              paidLater = JSON.parse(reader.result).paid_later_attempts || [];
            } catch {
              paidLater = [];
            }
            this.$store.commit("showSnackbar", {
              message: this.savedMessage("External marks saved successfully", paidLater),
              color: paidLater.length ? "warning" : "success",
            });
            this.closeUploadDialog();
            this.search();
          };
          reader.readAsText(res.data);
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message: err.response?.data?.message || "Upload failed",
            color: "error",
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },

    buildFormData(saveType) {
      const fd = new FormData();
      fd.append("file", this.uploadForm.file, this.uploadForm.file.name);
      fd.append("type", "external");
      fd.append("save", saveType);
      if (this.uploadYear) fd.append("exam_year", this.uploadYear);
      if (this.uploadMonth) fd.append("exam_month", this.uploadMonth);
      if (this.uploadForm.grace_mark !== null && this.uploadForm.grace_mark !== "") {
        fd.append("grace_mark", this.uploadForm.grace_mark);
      }

      return fd;
    },

    // 4xx = validation error (e.g. bad CSV headers) — parse JSON from blob
    showErrorResponse(blobData, fallback) {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const data = JSON.parse(reader.result);
          this.$store.commit("showSnackbar", {
            message: data.errors || data.message || fallback,
            color: "error",
          });
        } catch {
          this.$store.commit("showSnackbar", {
            message: fallback,
            color: "error",
          });
        }
      };
      reader.readAsText(blobData);
    },

    downloadErrorCsv(blobData) {
      const url = window.URL.createObjectURL(new Blob([blobData]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `external_errors.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      blobData.text().then((text) => {
        const rows = text.trim().split("\n").length - 1;
        this.$store.commit("showSnackbar", {
          message: `${rows} row(s) have errors. Nothing was saved. See the downloaded file.`,
          color: "error",
        });
      });
    },

    // A paper now passed whose newer attempt is already paid: that attempt is left
    // open for someone to refund or cancel.
    savedMessage(message, paidLater) {
      if (!paidLater.length) return message;
      const rows = paidLater
        .map((p) => `${p.usn_id} ${p.course_code} (${p.exam_batch})`)
        .join(", ");
      return `${message}. Paper passed but a paid later attempt is still open: ${rows}`;
    },

    // ── Edit ─────────────────────────────────────────────────────────────
    editMark(item) {
      this.editingItem = { ...item };
      this.editDialog = true;
    },

    async saveEdit() {
      if (!this.$refs.form.validate()) return;

      this.savingEdit = true;
      await this.$axios
        .put(`/external_marks/${this.editingItem.id}`, {
          external_mark: { mark: this.editingItem.mark },
        })
        .then((res) => {
          const paidLater = res.data?.paid_later_attempts || [];
          this.$store.commit("showSnackbar", {
            message: this.savedMessage("External mark updated", paidLater),
            color: paidLater.length ? "warning" : "success",
          });
          this.editDialog = false;
          this.search();
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message:
              (typeof err.response?.data?.errors === "string" &&
                err.response.data.errors) ||
              err.response?.data?.message ||
              "Update failed",
            color: "error",
          });
        })
        .finally(() => {
          this.savingEdit = false;
        });
    },
    downloadResult() {
      if (!this.resultCsv) return;
      this.downloadingResult = true;
      try {
        const binary = atob(this.resultCsv);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

        const url = window.URL.createObjectURL(
          new Blob([bytes], { type: "text/csv" }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "external_marks_result.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } finally {
        this.downloadingResult = false;
      }
    },
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
